/**
 * Record updates: the housekeeping suggestions (titles, owners, cadence,
 * mappings, shelves, frameworks, the company profile), before → after, with
 * Apply / Skip / Undo. Gap suggestions are findings and live in that queue.
 * The path stays /suggestions so existing links keep working.
 */
import { createFileRoute } from "@tanstack/react-router";

import { ComplianceOrganizationMissing, ComplianceUnavailable } from "../components/regulation/ComplianceUnavailable";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { PageHeader } from "../components/regulation/PageHeader";
import { RecordUpdatesPage } from "../components/regulation/recordUpdates/RecordUpdatesPage";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { useRecordUpdates } from "../state/recordUpdates";

function RecordUpdatesFrame({ children }: { readonly children: React.ReactNode }) {
  return (
    <div data-regulation-page="true" className="relative flex min-h-0 flex-1 flex-col">
      <PageHeader title="Suggestions" />
      <div className="px-4 py-6 sm:px-6">{children}</div>
    </div>
  );
}

function RecordUpdatesRoute() {
  const { org: orgSegment } = Route.useParams();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const view = useRecordUpdates(organizationId);
  if (isLoading) return <RecordUpdatesFrame><ListSkeleton rows={6} /></RecordUpdatesFrame>;
  if (!organizationId) return <RecordUpdatesFrame><ComplianceOrganizationMissing /></RecordUpdatesFrame>;
  if (view.unavailableReason) {
    return (
      <RecordUpdatesFrame>
        <ComplianceUnavailable reason={view.unavailableReason} onRetry={view.refresh} />
      </RecordUpdatesFrame>
    );
  }
  if (!view.ready) return <RecordUpdatesFrame><ListSkeleton rows={6} /></RecordUpdatesFrame>;
  return <RecordUpdatesPage key={organizationId} orgSegment={orgSegment} view={view} />;
}

export const Route = createFileRoute("/$org/compliance/suggestions")({
  component: RecordUpdatesRoute,
});
